import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { format } from 'date-fns'

import { useNotifications } from 'app/application/application'
import { useUser, useWorkspace } from 'app/user/application'
import { WorkspaceActivity } from 'app/user/types'

import { Chip, Table } from 'components'

import styles from './LastActivity.module.scss'

const LastActivity = () => {
  const { t } = useTranslation('dashboard')
  const { t: tConnections } = useTranslation('connections')
  const { createNotification, te } = useNotifications()
  const { getLastActivities } = useWorkspace()
  const { workspace } = useUser()
  const [activities, setActivities] = useState<WorkspaceActivity[]>([])
  const [state, setState] = useState<'loading' | 'ready' | 'error'>('loading')

  const getDetails = (activity: WorkspaceActivity) => {
    if (activity.type === 'model') {
      return t('tiles.lastActivity.events.model', activity.properties)
    }
    const properties = activity.properties
    const values = {
      ...properties,
      connection: tConnections(`available.${properties.connectionName}.displayName`),
    }
    const message =
      activity.type === 'upload'
        ? t('tiles.lastActivity.events.upload', values)
        : t(`tiles.lastActivity.events.${activity.properties.event}`, values)

    if (
      activity.type === 'signal' &&
      activity.properties.event === 'treatmentsUpdated' &&
      (activity.properties.added !== undefined || activity.properties.removed !== undefined)
    ) {
      return `${message} ${t('tiles.lastActivity.treatmentChanges', {
        added: activity.properties.added ?? 0,
        removed: activity.properties.removed ?? 0,
      })}`
    }
    return message
  }

  useEffect(() => {
    let active = true
    setActivities([])
    setState('loading')
    if (!workspace?.id) return

    void getLastActivities().then((result) => {
      if (!active) return
      if (result.ok) {
        setActivities(result.val)
        setState('ready')
      } else {
        setState('error')
        createNotification({
          title: te(`http.${result.val.code}`),
          message: result.val.message,
          type: 'error',
        })
      }
    })

    return () => {
      active = false
    }
  }, [createNotification, getLastActivities, te, workspace?.id])

  return (
    <div className={styles.activities} aria-busy={state === 'loading'}>
      <Table.Table
        className={styles.table}
        columns={[
          t('tiles.lastActivity.columns.date'),
          t('tiles.lastActivity.columns.details'),
          t('tiles.lastActivity.columns.status'),
        ]}
        data={activities}
        renderRow={(activity, index) => (
          <Table.Row key={`${activity.timestamp}-${index}`}>
            <Table.Cell className={styles.date}>
              <time dateTime={activity.timestamp}>
                {format(new Date(activity.timestamp), 'MMM d, hh:mm a')}
              </time>
            </Table.Cell>
            <Table.Cell>{getDetails(activity)}</Table.Cell>
            <Table.Cell className={styles.status}>
              <Chip
                color={
                  activity.type === 'upload'
                    ? activity.status === 'failed'
                      ? 'yellow'
                      : 'green'
                    : 'neutral'
                }
              >
                {activity.type === 'upload'
                  ? t(`tiles.lastActivity.${activity.status}`)
                  : t(`tiles.lastActivity.types.${activity.type}`)}
              </Chip>
            </Table.Cell>
          </Table.Row>
        )}
      />
      {activities.length === 0 && (
        <p className={styles.message}>{t(`tiles.lastActivity.${state}`)}</p>
      )}
    </div>
  )
}

export default LastActivity
